import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch } from "react-redux";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { editUserOverlay, suspendUser } from "../../../store/actions/adminActions";

const CustomHeader = ({ column }) => (
  <span className="text-xs font-semibold text-gray-600">{column.title}</span>
);

function Avatar({ name, src, size = 28 }) {
  const [errored, setErrored] = React.useState(false);

  const initials = (name || "")
    .split(" ")
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  if (!src || errored) {
    return (
      <div
        className="flex items-center justify-center rounded-full border border-gray-300 bg-gray-100 text-gray-500"
        style={{ width: size, height: size }}
        title={name}
      >
        <svg
          width={Math.floor(size * 0.6)}
          height={Math.floor(size * 0.6)}
          viewBox="0 0 20 20"
          xmlns="http://www.w3.org/2000/svg"
          fill="currentColor"
        >
          <path d="M10 10a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm0 1c-3.33 0-6 1.34-6 3v2h12v-2c0-1.66-2.67-3-6-3Z" />
        </svg>
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={name}
      title={name}
      loading="lazy"
      referrerPolicy="no-referrer"
      className="rounded-full object-cover border border-gray-300 bg-white"
      style={{ width: size, height: size }}
      onError={() => setErrored(true)}
    />
  );
}

const getFullName = (user) => `${user.first_name || ""} ${user.last_name || ""}`.trim() || "-";

const UserCell = ({ row }) => (
  <div className="flex items-center gap-2">
    <div className="h-7 w-7 rounded-md border border-gray-300 bg-gray-50 flex items-center justify-center">
      <svg className="w-4 h-4 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <circle cx="12" cy="8" r="3.2" strokeWidth="1.6" />
        <path d="M4 19c1.4-3.1 4.3-4.8 8-4.8s6.6 1.7 8 4.8" strokeWidth="1.6" />
      </svg>
    </div>
    <span className="text-sm text-gray-800">{getFullName(row.original)}</span>
  </div>
);

const ActivityCell = () => (
  <svg width="36" height="14" viewBox="0 0 36 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M1 10C4 10 3 2 6 2C9 2 8 13 11 13C14 13 13 4 16 4C19 4 18 12 21 12C24 12 23 3 26 3C29 3 28 11 31 11C33 11 33.5 8 35 8"
      stroke="#11B486"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const ActionMenu = ({ userId }) => {
  const dispatch = useDispatch();
  const wrapRef = useRef(null);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    const closeOnOutside = (event) => {
      const t = event.target;
      if (wrapRef.current?.contains(t) || dropdownRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", closeOnOutside);
    return () => document.removeEventListener("mousedown", closeOnOutside);
  }, []);

  useEffect(() => {
    if (!open || !buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const menuWidth = 176;
    const menuHeight = 84;
    const gap = 6;
    const viewportPadding = 8;

    const left = Math.min(
      window.innerWidth - menuWidth - viewportPadding,
      Math.max(viewportPadding, rect.right - menuWidth)
    );

    const canOpenDown = rect.bottom + gap + menuHeight <= window.innerHeight - viewportPadding;
    const top = canOpenDown
      ? rect.bottom + gap
      : Math.max(viewportPadding, rect.top - menuHeight - gap);

    setMenuPos({ top, left });
  }, [open]);

  const handleEdit = () => {
    dispatch(editUserOverlay(userId));
    setOpen(false);
  };

  const handleDelete = () => {
    confirmAlert({
      message: "Are you sure you want to suspend this user?",
      buttons: [
        { label: "Yes", onClick: () => dispatch(suspendUser(userId)) },
        { label: "No", onClick: () => void 0 },
      ],
    });
    setOpen(false);
  };

  return (
    <div className="relative" ref={wrapRef}>
      <button
        ref={buttonRef}
        type="button"
        className="h-8 w-8 rounded-full border border-gray-300 text-gray-600 hover:bg-gray-50"
        onClick={() => setOpen((prev) => !prev)}
        aria-label="Open user actions"
      >
        <svg className="mx-auto h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
          <circle cx="12" cy="6" r="1.6" />
          <circle cx="12" cy="12" r="1.6" />
          <circle cx="12" cy="18" r="1.6" />
        </svg>
      </button>
      {open &&
        createPortal(
          <div
            ref={dropdownRef}
            className="fixed z-[10050] w-44 rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
            style={{ top: `${menuPos.top}px`, left: `${menuPos.left}px` }}
            role="menu"
          >
            <button type="button" onClick={handleEdit} className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50">
              Edit User
            </button>

          </div>,
          document.body
        )}
    </div>
  );
};

export function getAdminUserColumns(t) {
  const StatusCell = ({ value }) => {
    const isActive = String(value || "").toLowerCase() === "active";
    const label = isActive
      ? t("adminManagement.users.statuses.active")
      : t("adminManagement.users.statuses.inactive");
    return (
      <span
        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${isActive ? "bg-[#0B0B0F] text-white" : "bg-gray-200 text-gray-700"}`}
      >
        {label}
      </span>
    );
  };

  const formatJoinDate = (row) => {
    const raw = row.join_date || row.created_at || row.createdAt || "";
    if (!raw) return "-";
    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) return String(raw);
    const day = `${parsed.getDate()}`.padStart(2, "0");
    const month = `${parsed.getMonth() + 1}`.padStart(2, "0");
    const year = parsed.getFullYear();
    return `${day}.${month}.${year}`;
  };

  const RoleCell = ({ value }) => {
    const raw = String(value || "").trim();
    if (!raw || raw === "-") {
      return <span className="text-sm text-gray-700">-</span>;
    }
    const key = `adminManagement.users.roles.${raw.toLowerCase()}`;
    const label = t(key, { defaultValue: raw });
    return <span className="text-sm text-gray-700">{label}</span>;
  };

  return [
    {
      Header: t("adminManagement.users.columns.user"),
      accessor: "first_name",
      Cell: ({ row }) => {
        const user = row.original;
        const fullName =
          `${user.first_name || ""} ${user.last_name || ""}`.trim() || "Unnamed User";

        return (
          <div className="flex items-center gap-3">
            <Avatar
              name={fullName}
              src={user.avatar}
              size={34}
            />

            <div className="min-w-0">
              <div className="text-[13px] font-medium text-[#111827] truncate">
                {fullName}
              </div>
              <div className="text-[12px] text-[#6B7280] truncate">
                {user.email || "-"}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      Header: CustomHeader,
      accessor: "email",
      title: t("adminManagement.users.columns.email"),
      Cell: ({ value }) => <span className="text-sm text-gray-700">{value || "-"}</span>,
    },
    {
      Header: CustomHeader,
      accessor: "department_name",
      title: t("adminManagement.users.columns.riskCenter"),
      Cell: ({ row }) => (
        <span className="text-sm text-gray-700">
          {row.original.department_name || t("adminManagement.users.notApplicable")}
        </span>
      ),
    },
    {
      Header: CustomHeader,
      accessor: "role",
      title: t("adminManagement.users.columns.role"),
      Cell: ({ value }) => <RoleCell value={value} />,
    },
    { Header: CustomHeader, accessor: "status", title: t("adminManagement.users.columns.status"), Cell: StatusCell },
    {
      Header: CustomHeader,
      accessor: "join_date",
      title: t("adminManagement.users.columns.joinDate"),
      Cell: ({ row }) => <span className="text-sm text-gray-700">{formatJoinDate(row.original)}</span>,
    },
    { Header: CustomHeader, accessor: "activity", title: t("adminManagement.users.columns.activity"), Cell: ActivityCell },
    {
      Header: CustomHeader,
      accessor: "actions",
      title: t("adminManagement.users.columns.actions"),
      Cell: ({ row }) => <ActionMenu userId={row.original.user_id} />,
    },
  ];
}
